import React, { useEffect, useMemo, useState } from "react";
import MainContainer from "../../components/MainContainer";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  HeaderContainer,
  LeftSideHeader,
  RightSideHeader,
  PlaylistName,
  PublicContainer,
  EditButton,
  GoBackButton,
  CategoryItem,
  DeleteButton,
  ContentContainer,
  Content,
  EmptyState,
  Button,
  Column,
  SkeletonUsername,
  SkeletonPlaylistName,
  SkeletonPublicBadge,
  StyledGoBackButton,
  RightSideHeaderRow,
} from "./PlaylistDetailPage.styles";
import { ReactComponent as ChevronIcon } from "../../assets/icons/chevronLeft.svg";
import EditPlaylistModal from "../../components/EditPlaylistModal/EditPlaylistModal";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPlaylist,
  removePlaylist,
  toggleFavoritePlaylist,
} from "../../store/slices/playlist/asyncThunk";
import VideoMasonrySkeletonLoader from "../../components/utils/VideoMasonrySkeletonLoader";
import ExploreItem from "../../components/ExploreItem/ExploreItem";
import {
  selectSelectedCollection,
  setSelectedCollection,
} from "../../store/slices/model/slice";

import { setSignUpModalOpen } from "../../store/slices/common/slice";
import { useMediaQuery } from "react-responsive";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import GenerateCollectionModal from "../../components/GenerateCollectionModal/GenerateCollectionModal";
import EditImageModal from "../../components/EditImageModal/EditImageModal";
import GroupModal from "../../components/GroupModal/GroupModal";
import {
  resetPlaylist,
  selectPlaylist,
  selectPlaylistPending,
} from "../../store/slices/playlist/slice";
import { selectUser } from "../../store/slices/user/slice";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import { toast } from "sonner";
import LottieIcon from "../../components/LottieIcon/LottieIcon";
import likeAnimation from "../../assets/animations/like2.json";
import editAnimation from "../../assets/animations/edit.json";
import deleteAnimation from "../../assets/animations/trash.json";
import { isImageOutput } from "../../utils/utils";
import CreateImageModal from "../../components/CreateImageModal/CreateImageModal";
import { BLEND_MODEL, CREATE_IMAGE_MODEL_NAME } from "../../utils/constants";

const PlaylistDetailPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { id } = useParams();

  useEffect(() => {
    if (id) {
      dispatch(fetchPlaylist({ playlistId: id }));
    }
  }, [dispatch, id]);

  const isDesktop = useMediaQuery({ maxWidth: 1224 });
  const isTable = useMediaQuery({ maxWidth: 950 });
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const selectedCollectionVideo = useSelector(selectSelectedCollection);
  const { data, loading, error } = useSelector(selectPlaylist);
  const loggedIn = useSelector(selectLoggedIn);
  const [isOpenEditPlaylistModal, setIsOpenEditPlaylistModal] = useState(false);
  const [isGenerateCollectionModalOpen, setIsGenerateCollectionModalOpen] =
    useState(false);
  const [isEditImageModalOpen, setIsEditImageModalOpen] = useState(false);
  const [isCreateImageModalOpen, setIsCreateImageModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const deletePlaylistPending = useSelector(
    selectPlaylistPending,
  ).removePlaylist;

  const numOfColumns = useMemo(() => {
    switch (true) {
      case isMobile:
        return 2;
      case isTable:
        return 3;
      case isDesktop:
        return 4;
      default:
        return 5;
    }
  }, [isDesktop, isTable, isMobile]);

  const handleGoBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    return () => {
      dispatch(resetPlaylist());
    };
  }, [dispatch]);

  const handleEdit = () => {
    setIsOpenEditPlaylistModal(true);
  };

  const handleDelete = () => {
    dispatch(removePlaylist({ playlistId: id }))
      .unwrap()
      .then(() => {
        setShowDeleteConfirm(false);
        navigate(-1);
      })
      .catch((err) => {
        console.log(err);
        setShowDeleteConfirm(false);
        toast.error(err.message);
      });
  };

  const handleFavoriteToggle = async () => {
    dispatch(toggleFavoritePlaylist({ playlistId: id }));
  };

  const isHeaderLoading = loading && !data?.name;

  return (
    <MainContainer hideTabs fullWidth>
      <Container>
        <HeaderContainer>
          <LeftSideHeader>
            <GoBackButton onClick={handleGoBack} $width={36} $noPadding>
              <ChevronIcon />
            </GoBackButton>
            {isHeaderLoading ? (
              <>
                <SkeletonPlaylistName />
                <SkeletonUsername />
                <SkeletonPublicBadge />
              </>
            ) : (
              <>
                {data?.name && <PlaylistName>{data?.name}</PlaylistName>}
                {data?.username && (
                  <PublicContainer>@{data?.username}</PublicContainer>
                )}
                {data?.isPublic && (
                  <PublicContainer $outline>Public</PublicContainer>
                )}
              </>
            )}
          </LeftSideHeader>
          <RightSideHeader>
            <StyledGoBackButton onClick={handleGoBack} $width={36} $noPadding>
              <ChevronIcon />
            </StyledGoBackButton>
            <RightSideHeaderRow>
              {!isHeaderLoading && user?._id === data?.user && (
                <DeleteButton
                  title="Delete"
                  onClick={() => {
                    setShowDeleteConfirm(true);
                  }}
                >
                  <LottieIcon
                    animationData={deleteAnimation}
                    trigger="hover"
                    size={24}
                  />
                </DeleteButton>
              )}
              {!isHeaderLoading && user?._id === data?.user && (
                <EditButton onClick={handleEdit} title="Edit">
                  <LottieIcon
                    animationData={editAnimation}
                    trigger="hover"
                    strokeColor={"#fff"}
                  />
                </EditButton>
              )}
              {!isHeaderLoading && (
                <CategoryItem
                  $width={44}
                  title={
                    user?.favoritePlaylists?.includes(data?._id)
                      ? "Remove from favorites"
                      : "Add to favorites"
                  }
                >
                  <LottieIcon
                    animationData={likeAnimation}
                    trigger="toggle"
                    isToggled={user?.favoritePlaylists?.includes(data?._id)}
                    onToggle={handleFavoriteToggle}
                    likeSegment={[1, 90]}
                    unlikeSegment={[90, 180]}
                    size={56}
                    absolute
                  />
                </CategoryItem>
              )}
            </RightSideHeaderRow>
          </RightSideHeader>
        </HeaderContainer>
        <ContentContainer>
          {error ? (
            <Content>
              <EmptyState>
                <h3>We're having trouble loading videos</h3>
                <p>
                  This is usually temporary. Please check your connection and
                  try again.
                </p>
                <Button
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  Retry
                </Button>
              </EmptyState>
            </Content>
          ) : loading || !data?.collections ? (
            <VideoMasonrySkeletonLoader
              count={24}
              fullWidth
              mobileTwoRows
              offInlinePadding
            />
          ) : (
            <Content>
              {!data?.collections?.length ? (
                <EmptyState>
                  <h3>No videos found</h3>
                  <p>There are no videos to display at the moment.</p>
                </EmptyState>
              ) : (
                Array.from({ length: numOfColumns }).map((_, colIndex) => {
                  return (
                    <Column key={colIndex}>
                      {data?.collections
                        .filter((_, i) => i % numOfColumns === colIndex)
                        .map((item) => {
                          return (
                            <ExploreItem
                              key={item._id}
                              item={item}
                              onItemClick={() => {
                                if (loggedIn) {
                                  dispatch(setSelectedCollection(item));
                                  if (
                                    item.modelType === CREATE_IMAGE_MODEL_NAME
                                  ) {
                                    setIsCreateImageModalOpen(true);
                                  } else if (item.modelType === BLEND_MODEL) {
                                    setIsGroupModalOpen(true);
                                  } else if (
                                    isImageOutput(
                                      item.modelType ?? item.modelName,
                                    )
                                  ) {
                                    setIsEditImageModalOpen(true);
                                  } else {
                                    setIsGenerateCollectionModalOpen(true);
                                  }
                                } else {
                                  dispatch(setSignUpModalOpen(true));
                                }
                              }}
                            />
                          );
                        })}
                    </Column>
                  );
                })
              )}
            </Content>
          )}
        </ContentContainer>
      </Container>
      <EditPlaylistModal
        isOpen={isOpenEditPlaylistModal}
        onClose={() => setIsOpenEditPlaylistModal(false)}
      />
      <GenerateCollectionModal
        isOpen={isGenerateCollectionModalOpen}
        onClose={() => setIsGenerateCollectionModalOpen(false)}
        playlistId={id}
      />

      <EditImageModal
        isOpen={isEditImageModalOpen}
        collection={selectedCollectionVideo}
        onClose={() => setIsEditImageModalOpen(false)}
        playlistId={id}
      />

      <CreateImageModal
        isOpen={isCreateImageModalOpen}
        collection={selectedCollectionVideo}
        onClose={() => setIsCreateImageModalOpen(false)}
        playlistId={id}
      />

      <GroupModal
        isOpen={isGroupModalOpen}
        onClose={() => setIsGroupModalOpen(false)}
        playlistId={id}
      />

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Playlist"
        message={
          "Are you sure you want to delete these playlist? This action cannot be undone."
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonType="danger"
        isLoading={deletePlaylistPending}
        theme="dark"
      />
    </MainContainer>
  );
};

export default PlaylistDetailPage;
